import { RoleId } from '@common/constants';
import {
    CONTACT_BLOCK_PRESET_KINDS,
    CONTACT_BLOCK_PRESETS,
    type ContactBlockPresetKind,
    createContactBlockPresetConfig,
} from '@common/constants/contactBlockPresets';
import { DEFAULT_TEMPLATE_FORM_LANGUAGE } from '@common/i18n/constants';
import {
    type ContactBlockConfig,
    CURRENCY_VALUE_TYPES,
    type CurrencyValueType,
    DEFAULT_UPLOAD_FILE_COUNT,
    NON_NESTABLE_QUESTION_TYPES,
    QUESTION_TYPES,
    type QuestionOption,
    type QuestionType,
    type QuestionValidationSchema,
    type SelectOption,
    type TableFormColumn,
    type TemplateFormFieldUpdate,
} from '@common/types/templateFormFields';
import type { TemplateFormLanguage } from '@common/types/templateForms';
import { DEFAULT_CONTACT_BLOCK_CONFIG, getContactBlockConfig } from '@common/utils/contactBlock';
import { getIsIntakeFormV2 } from '@common/utils/intakeFormVersion';
import { INTAKE_PREFILL_TAG_OPTIONS, getIntakePrefillTagLabel } from '@common/utils/matterFields';
import { AddCircleOutlineOutlined } from '@mui/icons-material';
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline';
import {
    Box,
    Button,
    Checkbox,
    createFilterOptions,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    type SelectChangeEvent,
    TextField,
    Typography,
} from '@mui/material';
import isEmpty from 'lodash/isEmpty';
import { type MutableRefObject, useEffect, useLayoutEffect, useState } from 'react';
import AutoCompleteMultiSelect from '@/components/AutoCompleteMultiselect';
import useIdentityStore from '@/stores/useIdentityStore';
import { generateEightDigitsNumber } from '@/utils';
import { errorSnackbar } from '@/utils/snackbar';
import { groupConditionsByValue } from './conditions';
import ContactBlockConfigEditor, { isContactBlockConfigValid } from './ContactBlockConfigEditor';
import QuestionChoiceComponent from './QuestionChoiceComponent';
import QuestionSelectComponent from './QuestionSelectComponent';
import TableColumnsComponent from './TableColumnsComponent';
import TableRowCountField from './TableRowCountField';
import type { OptionErrorType } from './types';
import UploadFileCountField from './UploadFileCountField';

/** A conditional question's choices: options for radio and multiselect, items for a dropdown */
interface ConditionalChoiceOption extends QuestionOption, SelectOption {}

/** The free-text fields of a conditional question */
type ConditionTextField = 'title' | 'subTitle' | 'typeOfDocument';

interface Condition {
    showOptions: boolean;
    optionValue: string;
    showConditionalType: boolean;
    conditionalTypeValue: string;
    title: string;
    subTitle: string;
    name: string;
    conditionalChoiceOptions?: ConditionalChoiceOption[];
    typeOfDocument?: string;
    questionValidationSchema?: QuestionValidationSchema;
    conditionalColumns?: TableFormColumn[];
    conditionalRowCount?: number | null;
    valueType?: CurrencyValueType;
    tags?: string[];
}

const defaultFieldData: TemplateFormFieldUpdate = {
    templateFormFieldId: undefined,
    displayOrder: 0,
    isVisibleToUser: true,
    name: '',
    title: '',
    subTitle: '',
    questionSchema: {
        type: 'text',
        name: '',
        label: '',
        value: '',
        placeholder: '',
        order: 1,
        formGroupTitle: '',
        isCustomQuestion: true,
        conditions: [],
        tags: [],
    },
    questionValidationSchema: {},
    isLocked: false,
    formGroupTitle: '',
};

/** Type dropdown value for a contact block preset; the saved question type stays `contactInfoSection`. */
function getContactBlockPresetOptionValue(kind: ContactBlockPresetKind): string {
    return `contactInfoSection:${kind}`;
}

/** Narrows a select's value (typed `string` by MUI) to a currency value type, or '' for N/A. */
function toCurrencyValueType(value: string): CurrencyValueType | '' {
    return CURRENCY_VALUE_TYPES.find((type) => type === value) ?? '';
}

/** Older questions can store their description as lines; the description field edits one string. */
function getDescriptionText(description: string | string[] | undefined): string | undefined {
    return Array.isArray(description) ? description.join('\n') : description;
}

/** Upload questions saved without a file count accept a single file */
const UNSET_UPLOAD_FILE_COUNT = 1;

const UPLOAD_FILE_COUNT_DESCRIPTION_PATTERN = /Max\. number of files - \d+/;

function getUploadDescription(fileCount: number): string {
    return `Max. number of files - ${fileCount} \n Max. file size - 10 MB \n Allowed file types: DOC, PDF, PNG, JPG`;
}

/** Keeps the default upload description in step with the file count; custom descriptions are left alone */
function withUploadFileCount(description: string | undefined, fileCount: number) {
    return description?.replace(
        UPLOAD_FILE_COUNT_DESCRIPTION_PATTERN,
        `Max. number of files - ${fileCount}`,
    );
}

function getUploadValidationSchema(fileCount: number): QuestionValidationSchema {
    return {
        onChange: {
            fileSize: 10 * 1024 * 1024, // 10MB in bytes
            fileType: [
                'application/msword',
                'application/pdf',
                'image/jpeg',
                'image/jpg',
                'image/png',
            ],
            fileCount,
        },
    };
}

const filterPrefillTagOptions = createFilterOptions<string>({
    stringify: (option) => `${getIntakePrefillTagLabel(option)} ${option}`,
});

export interface FromScratchQuestionsTabHandle {
    submit(): void;
}

interface Props {
    previousQuestionOrder: number;
    formField?: TemplateFormFieldUpdate;
    formGroup?: number;
    formGroupTitle?: string;
    practiceArea?: string | null;
    matterType?: string | null;
    isIntakeForm?: boolean;
    isNestedQuestion?: boolean;
    language?: TemplateFormLanguage;
    submitRef?: MutableRefObject<FromScratchQuestionsTabHandle | null>;
    onConfirm: (fieldData: TemplateFormFieldUpdate | TemplateFormFieldUpdate[]) => void;
    onSubmitDisabledChange: (disabled: boolean) => void;
}

export default function FromScratchQuestionsTab({
    formField,
    formGroup,
    formGroupTitle,
    previousQuestionOrder,
    practiceArea,
    matterType,
    isIntakeForm,
    isNestedQuestion,
    language = DEFAULT_TEMPLATE_FORM_LANGUAGE,
    submitRef,
    onConfirm,
    onSubmitDisabledChange,
}: Props) {
    const user = useIdentityStore((state) => state.user);
    const isSuperAdmin = !!user?.roles?.some((role) => role.roleId === RoleId.SUPER_ADMIN);
    const questionConstantType = ['text', 'currency', 'percentage', 'date', 'address'];
    const isIntakeFormV2 = getIsIntakeFormV2(practiceArea, matterType);
    const isRealEstateIntakeForm = practiceArea === 'RealEstate';
    const showPrefillTags = isRealEstateIntakeForm && !isNestedQuestion;
    const isContactBlockAllowed = !!isIntakeForm && isIntakeFormV2;
    const isTableAllowed = isIntakeFormV2 || !!isIntakeForm;
    const questionOptions: {
        value: QuestionType;
        label: string;
        isDisabled: boolean;
    }[] = [
        {
            label: 'Multiple choice',
            value: 'radio',
            isDisabled: !!formField,
        },
        {
            label: 'Multi select',
            value: 'multiselect',
            isDisabled: !!formField,
        },
        {
            label: 'Free text',
            value: 'text',
            isDisabled: false,
        },
        {
            label: 'Free text (Multiline)',
            value: 'textarea',
            isDisabled: false,
        },
        {
            label: 'Upload field',
            value: 'upload',
            isDisabled: !!formField,
        },
        {
            label: 'Date picker',
            value: 'date',
            isDisabled: false,
        },
        {
            label: 'Currency field',
            value: 'currency',
            isDisabled: false,
        },
        {
            label: 'Percentage field',
            value: 'percentage',
            isDisabled: false,
        },
        {
            label: 'Address field',
            value: 'address',
            isDisabled: false,
        },
        {
            label: 'Dropdown',
            value: 'select',
            isDisabled: !!formField,
        },
        {
            label: 'Table',
            value: 'table',
            isDisabled: !!formField || !isTableAllowed,
        },
        {
            label: 'Contact block',
            value: 'contactInfoSection',
            isDisabled: !!formField || !isContactBlockAllowed,
        },
    ];
    const selectQuestionOptions = isNestedQuestion
        ? questionOptions.filter((op) => !NON_NESTABLE_QUESTION_TYPES.includes(op.value))
        : questionOptions;
    const conditionalQuestionOptions = selectQuestionOptions.filter(
        (op) => op.value !== 'contactInfoSection' || isContactBlockAllowed,
    );

    const [conditions, setConditions] = useState<Condition[]>([]);

    const handleConditionalOnChange = (index: number, field: ConditionTextField, value: string) => {
        const newConditions = [...conditions];

        newConditions[index] = {
            ...newConditions[index],
            [field]: value,
            name: createFieldName(newConditions[index].conditionalTypeValue),
        };
        setConditions(newConditions);
    };

    const handleAddConditional = () => {
        setConditions((prevState) => {
            const newConditions = [
                ...prevState,
                {
                    showOptions: true,
                    optionValue: '',
                    showConditionalType: false,
                    conditionalTypeValue: 'text',
                    title: '',
                    subTitle: '',
                    name: '',
                    conditionalChoiceOptions: [],
                },
            ];
            return newConditions;
        });
    };

    const handleRemoveConditional = () => {
        setConditions((prevState) => {
            if (prevState.length === 0) return prevState;
            const newConditions = prevState.slice(0, -1);
            return newConditions;
        });
    };

    const handleConditionalChange = (event: SelectChangeEvent<string>, index: number) => {
        const { value } = event.target;
        setConditions((prevState) =>
            prevState.map((condition, i) =>
                i === index
                    ? { ...condition, optionValue: value, showConditionalType: true }
                    : condition,
            ),
        );
    };

    const handleSecondSelectChange = (event: SelectChangeEvent<string>, index: number) => {
        const { value } = event.target;

        setConditions((prevState) =>
            prevState.map((condition, i) => {
                if (i === index) {
                    const updatedCondition = {
                        ...condition,
                        conditionalTypeValue: value,
                        subTitle:
                            value === 'upload'
                                ? getUploadDescription(DEFAULT_UPLOAD_FILE_COUNT)
                                : '',
                    };

                    // Set validation for conditional upload question
                    if (value === 'upload') {
                        updatedCondition.questionValidationSchema =
                            getUploadValidationSchema(DEFAULT_UPLOAD_FILE_COUNT);
                    }

                    // Initialize columns for conditional table question
                    if (value === 'table') {
                        updatedCondition.conditionalColumns = [
                            { key: '', label: '', type: 'text' },
                        ];
                        updatedCondition.conditionalRowCount = undefined;
                    } else {
                        updatedCondition.conditionalColumns = undefined;
                        updatedCondition.conditionalRowCount = undefined;
                    }

                    updatedCondition.valueType = undefined;

                    return updatedCondition;
                }
                return condition;
            }),
        );
    };

    const [isOptionError, setIsOptionError] = useState<OptionErrorType[]>([]);
    // The drawer unmounts this tab when it closes, so each opening starts from fresh state
    const [fieldData, setFieldData] = useState<TemplateFormFieldUpdate>(() =>
        formField
            ? {
                  ...formField,
                  subTitle: getDescriptionText(formField.questionSchema.description),
              }
            : defaultFieldData,
    );
    const [contactBlockPreset, setContactBlockPreset] = useState<ContactBlockPresetKind | null>(
        null,
    );
    const createFieldName = (type: string) => `${type}_${generateEightDigitsNumber()}`;

    const onChangeTitle = (value: string) => {
        if (formField) {
            setFieldData((prev) => ({
                ...prev,
                title: value,
                questionSchema: {
                    ...prev.questionSchema,
                    label: value,
                },
            }));
            return;
        }
        const createdName = createFieldName(fieldData.questionSchema?.type);
        setFieldData((prev) => ({
            ...prev,
            title: value,
            name: createdName,
            formGroupTitle: formGroupTitle,
            displayOrder: previousQuestionOrder + 1,
            questionSchema: {
                ...prev.questionSchema,
                label: value,
                name: createdName,
                formGroup: formGroup,
                formGroupTitle: formGroupTitle,
                order: previousQuestionOrder + 1,
            },
        }));
    };

    const onChangeDescription = (value: string) => {
        if (formField) {
            setFieldData((prev) => ({
                ...prev,
                subTitle: value,
                questionSchema: {
                    ...prev.questionSchema,
                    description: value,
                },
            }));
            return;
        }
        setFieldData((prev) => ({
            ...prev,
            subTitle: value,
            formGroupTitle: formGroupTitle,
            displayOrder: previousQuestionOrder + 1,
            questionSchema: {
                ...prev.questionSchema,
                description: value,
                formGroup: formGroup,
                formGroupTitle: formGroupTitle,
                order: previousQuestionOrder + 1,
            },
        }));
    };

    const onSelectContactBlockPreset = (kind: ContactBlockPresetKind) => {
        const preset = CONTACT_BLOCK_PRESETS[kind];
        setContactBlockPreset(kind);
        setFieldData((prev) => {
            const title = prev.title.trim() ? prev.title : preset.title;
            const name = prev.name || createFieldName('contactInfoSection');
            return {
                ...prev,
                title,
                name,
                subTitle: '',
                formGroupTitle: formGroupTitle,
                displayOrder: previousQuestionOrder + 1,
                questionSchema: {
                    ...prev.questionSchema,
                    type: 'contactInfoSection',
                    name,
                    label: title,
                    description: '',
                    formGroup: formGroup,
                    formGroupTitle: formGroupTitle,
                    order: previousQuestionOrder + 1,
                    columns: undefined,
                    rowCount: undefined,
                    valueType: undefined,
                    contactBlock: createContactBlockPresetConfig(kind),
                },
                questionValidationSchema: { required: preset.isRequired },
            };
        });
    };

    const onChangeType = (value: string) => {
        const presetKind = CONTACT_BLOCK_PRESET_KINDS.find(
            (kind) => value === getContactBlockPresetOptionValue(kind),
        );
        if (presetKind) {
            onSelectContactBlockPreset(presetKind);
            return;
        }
        const type = QUESTION_TYPES.find((questionType) => questionType === value);
        if (!type) return;
        setContactBlockPreset(null);
        if (type === 'upload') {
            // If selected type is 'upload' then:
            // set a default validation and
            // set a default description describing the supported files
            setFieldData((prev) => ({
                ...prev,
                subTitle: getUploadDescription(DEFAULT_UPLOAD_FILE_COUNT),
                questionSchema: {
                    ...prev.questionSchema,
                    type,
                    description: getUploadDescription(DEFAULT_UPLOAD_FILE_COUNT),
                    columns: undefined,
                    rowCount: undefined,
                    valueType: undefined,
                    contactBlock: undefined,
                },
                questionValidationSchema: getUploadValidationSchema(DEFAULT_UPLOAD_FILE_COUNT),
            }));
        } else if (type === 'table') {
            // If selected type is 'table', initialize with one empty column
            setFieldData((prev) => ({
                ...prev,
                subTitle: '',
                questionSchema: {
                    ...prev.questionSchema,
                    type,
                    description: '',
                    columns: [{ key: '', label: '', type: 'text' }],
                    rowCount: undefined,
                    valueType: undefined,
                    contactBlock: undefined,
                },
            }));
        } else if (type === 'contactInfoSection') {
            setFieldData((prev) => ({
                ...prev,
                subTitle: '',
                questionSchema: {
                    ...prev.questionSchema,
                    type,
                    description: '',
                    columns: undefined,
                    rowCount: undefined,
                    valueType: undefined,
                    contactBlock: { ...DEFAULT_CONTACT_BLOCK_CONFIG, questions: [] },
                },
                questionValidationSchema: {},
            }));
        } else if (type === 'currency' && isIntakeFormV2) {
            setFieldData((prev) => ({
                ...prev,
                subTitle: '',
                questionSchema: {
                    ...prev.questionSchema,
                    type,
                    description: '',
                    columns: undefined,
                    rowCount: undefined,
                    valueType: undefined,
                    contactBlock: undefined,
                },
            }));
        } else {
            setFieldData((prev) => ({
                ...prev,
                subTitle: '', //reset description for non-upload types
                questionSchema: {
                    ...prev.questionSchema,
                    type,
                    description: '',
                    columns: undefined,
                    rowCount: undefined,
                    valueType: undefined,
                    contactBlock: undefined,
                },
            }));
        }
    };

    const onChangeValueType = (value: CurrencyValueType | '') => {
        setFieldData((prev) => ({
            ...prev,
            questionSchema: {
                ...prev.questionSchema,
                valueType: value || undefined,
            },
        }));
    };

    const onChangeConditionalValueType = (index: number, value: CurrencyValueType | '') => {
        setConditions((prevState) =>
            prevState.map((condition, i) =>
                i === index
                    ? {
                          ...condition,
                          valueType: value || undefined,
                      }
                    : condition,
            ),
        );
    };

    const onChangeConditionalTags = (index: number, value: string[]) => {
        setConditions((prevState) =>
            prevState.map((condition, i) =>
                i === index
                    ? {
                          ...condition,
                          tags: value,
                      }
                    : condition,
            ),
        );
    };

    const onChangeTags = (value: string[]) => {
        setFieldData((prev) => ({
            ...prev,
            questionSchema: {
                ...prev.questionSchema,
                tags: value,
            },
        }));
    };

    const onChangeTableColumns = (columns: TableFormColumn[]) => {
        setFieldData((prev) => ({
            ...prev,
            questionSchema: {
                ...prev.questionSchema,
                columns: columns,
            },
        }));
    };

    const onChangeContactBlock = (contactBlock: ContactBlockConfig) => {
        setFieldData((prev) => ({
            ...prev,
            questionSchema: {
                ...prev.questionSchema,
                contactBlock,
            },
        }));
    };

    const onChangeChoiceOptions = (options: QuestionOption[]) => {
        setFieldData((prev) => ({
            ...prev,
            questionSchema: {
                ...prev.questionSchema,
                options: options,
            },
        }));
    };
    const onChangeSelectOptions = (options: SelectOption[]) => {
        setFieldData((prev) => ({
            ...prev,
            questionSchema: {
                ...prev.questionSchema,
                selectOptions: options,
            },
        }));
    };
    const onChangeTypeOfDocument = (typeOfDocument: string) => {
        setFieldData((prev) => ({
            ...prev,
            questionSchema: { ...prev.questionSchema, typeOfDocument },
        }));
    };

    const onChangeUploadFileCount = (fileCount: number) => {
        setFieldData((prev) => ({
            ...prev,
            subTitle: withUploadFileCount(prev.subTitle ?? undefined, fileCount),
            questionSchema: {
                ...prev.questionSchema,
                description: withUploadFileCount(
                    getDescriptionText(prev.questionSchema.description),
                    fileCount,
                ),
            },
            questionValidationSchema: {
                ...prev.questionValidationSchema,
                onChange: { ...prev.questionValidationSchema?.onChange, fileCount },
            },
        }));
    };

    const onChangeConditionalUploadFileCount = (index: number, fileCount: number) => {
        setConditions((prevState) =>
            prevState.map((condition, i) =>
                i === index
                    ? {
                          ...condition,
                          subTitle: withUploadFileCount(condition.subTitle, fileCount) ?? '',
                          questionValidationSchema: {
                              ...condition.questionValidationSchema,
                              onChange: {
                                  ...condition.questionValidationSchema?.onChange,
                                  fileCount,
                              },
                          },
                      }
                    : condition,
            ),
        );
    };

    const onChangeRequired = (required: boolean) => {
        setFieldData((prev) => ({
            ...prev,
            questionValidationSchema: { ...prev.questionValidationSchema, required },
        }));
    };

    const onChangeLocked = (isLocked: boolean) => {
        setFieldData((prev) => ({ ...prev, isLocked }));
    };

    const onChangeTableRowCount = (rowCount: number | undefined) => {
        setFieldData((prev) => ({
            ...prev,
            questionSchema: { ...prev.questionSchema, rowCount },
        }));
    };

    const getIsSubmitDisabled = () => {
        const hasEmptyConditionTitle = conditions.some((condition) => isEmpty(condition.title));

        const hasInsufficientConditionalOptions = conditions.some(
            (condition) =>
                (condition.conditionalTypeValue === 'radio' ||
                    condition.conditionalTypeValue === 'multiselect') &&
                (!condition.conditionalChoiceOptions ||
                    condition.conditionalChoiceOptions.length < 2 ||
                    condition.conditionalChoiceOptions.some((option) => !option.optionLabel)),
        );

        const hasInsufficientConditionalOptionsSelect = conditions.some(
            (condition) =>
                condition.conditionalTypeValue === 'select' &&
                (!condition.conditionalChoiceOptions ||
                    condition.conditionalChoiceOptions.length < 2 ||
                    condition.conditionalChoiceOptions.some((option) => !option.selectItemValue)),
        );

        const hasInvalidConditionalTableColumns = conditions.some((condition) => {
            if (condition.conditionalTypeValue !== 'table') return false;
            const columns = condition.conditionalColumns || [];
            const hasEmptyColumnLabel = columns.some((col) => isEmpty(col.label));
            const hasDuplicateLabels = columns.some((col, idx: number) =>
                columns.slice(0, idx).some((prevCol) => prevCol.label === col.label && col.label),
            );
            const hasSelectWithoutOptions = columns.some(
                (col) =>
                    col.type === 'select' && (!col.selectOptions || col.selectOptions.length < 2),
            );
            return (
                columns.length === 0 ||
                hasEmptyColumnLabel ||
                hasDuplicateLabels ||
                hasSelectWithoutOptions
            );
        });

        const hasEmptyConditionOptions = conditions.some((condition) =>
            condition.conditionalChoiceOptions?.some(
                (option) => isEmpty(option.optionLabel) && isEmpty(option.selectItemValue),
            ),
        );

        //check if any condition's optionValue is not in the options array
        const hasInvalidOptionValue = conditions.some(
            (condition) =>
                fieldData.questionSchema?.options &&
                !fieldData.questionSchema?.options.some(
                    (option) => option.optionLabel === condition.optionValue,
                ),
        );

        if (fieldData.questionSchema?.type === 'text') {
            return isEmpty(fieldData.title) || hasEmptyConditionTitle;
        }

        if (fieldData.questionSchema?.type === 'contactInfoSection') {
            return (
                isEmpty(fieldData.title) ||
                !isContactBlockConfigValid(getContactBlockConfig(fieldData.questionSchema))
            );
        }

        if (fieldData.questionSchema?.type === 'table') {
            const columns = fieldData.questionSchema?.columns || [];
            const hasEmptyColumnLabel = columns.some((col) => isEmpty(col.label));
            const hasDuplicateLabels = columns.some((col, index: number) =>
                columns.slice(0, index).some((prevCol) => prevCol.label === col.label && col.label),
            );
            const hasSelectWithoutOptions = columns.some(
                (col) =>
                    col.type === 'select' && (!col.selectOptions || col.selectOptions.length < 2),
            );
            return (
                isEmpty(fieldData.title) ||
                columns.length === 0 ||
                hasEmptyColumnLabel ||
                hasDuplicateLabels ||
                hasSelectWithoutOptions
            );
        }

        return (
            isEmpty(fieldData.title) ||
            (!!fieldData.questionSchema?.options?.find((op) => isEmpty(op?.optionLabel)) &&
                !!fieldData.questionSchema?.selectOptions?.find((op) =>
                    isEmpty(op?.selectItemValue),
                )) ||
            isOptionError.length > 0 ||
            ((fieldData.questionSchema?.type === 'radio' ||
                fieldData.questionSchema?.type === 'multiselect') &&
                (!fieldData.questionSchema?.options ||
                    fieldData.questionSchema?.options.length < 2)) ||
            (fieldData.questionSchema?.type === 'select' &&
                (!fieldData.questionSchema?.selectOptions ||
                    fieldData.questionSchema?.selectOptions.length < 2)) ||
            (conditions.length > 0 &&
                (hasEmptyConditionTitle ||
                    hasInsufficientConditionalOptions ||
                    hasEmptyConditionOptions ||
                    (fieldData.name === 'purchasersListedOnPurchaseAndSaleAgreement' ||
                    fieldData.name === 'borrowersList' ||
                    fieldData.name === 'sellersListedOnPurchaseAndSaleAgreement'
                        ? false
                        : hasInvalidOptionValue) ||
                    hasInsufficientConditionalOptionsSelect ||
                    hasInvalidConditionalTableColumns))
        );
    };
    const isSubmitDisabled = getIsSubmitDisabled();

    const copyFieldData = { ...fieldData };
    copyFieldData.questionSchema = { ...fieldData.questionSchema };

    copyFieldData.questionSchema.conditions = formField?.questionSchema?.conditions || [];

    if (conditions?.length > 0) {
        const newConditions = conditions.map((con) => {
            return {
                value: con?.optionValue,
                notes: `If '${con?.optionValue}' selected:`,
                nextQuestion: {
                    name: con?.name,
                },
            };
        });

        copyFieldData.questionSchema.conditions = [
            ...copyFieldData.questionSchema.conditions,
            ...newConditions,
        ];
    }
    copyFieldData.questionSchema.conditions = groupConditionsByValue(
        copyFieldData.questionSchema.conditions,
    );
    const payload = [
        {
            ...copyFieldData,
            questionSchema: {
                ...copyFieldData.questionSchema,
                ...(copyFieldData.questionSchema.type === 'radio' && {
                    radioOrientation: 'horizontal',
                }),
            },
            questionValidationSchema: copyFieldData.questionValidationSchema,
        },
        ...conditions.map((condition) => ({
            title: condition.title,
            name: condition.name,
            formGroupTitle: copyFieldData.formGroupTitle,
            displayOrder: copyFieldData.displayOrder,
            subTitle: condition.subTitle,
            isLocked: false,
            isVisibleToUser: false,
            questionSchema: {
                label: condition.title,
                name: condition.name,
                type: condition.conditionalTypeValue,
                formGroup: formGroup,
                formGroupTitle: copyFieldData.formGroupTitle,
                description: condition.subTitle,
                order: copyFieldData.displayOrder,
                ...((condition.conditionalTypeValue === 'radio' ||
                    condition.conditionalTypeValue === 'multiselect') && {
                    options: condition.conditionalChoiceOptions,
                }),
                ...(condition.conditionalTypeValue === 'select' && {
                    selectOptions: condition.conditionalChoiceOptions,
                }),
                isConditionalQuestion: true,
                typeOfDocument: condition.typeOfDocument,
                conditionedBy: copyFieldData.name,
                note: `If '${condition.optionValue}' Selected: `,
                conditionType: 'equals',
                conditionValue: condition.optionValue,
                ...(condition.conditionalTypeValue === 'currency' &&
                    isIntakeFormV2 &&
                    condition.valueType && {
                        valueType: condition.valueType,
                    }),
                ...(isRealEstateIntakeForm &&
                    condition.tags?.length && {
                        tags: condition.tags,
                    }),
                ...(condition.conditionalTypeValue === 'radio' && {
                    radioOrientation: 'horizontal',
                }),
                ...(condition.conditionalTypeValue === 'table' && {
                    columns: condition.conditionalColumns,
                    rowCount: condition.conditionalRowCount,
                }),
                ...(condition.conditionalTypeValue === 'contactInfoSection' && {
                    contactBlock: { ...DEFAULT_CONTACT_BLOCK_CONFIG, questions: [] },
                }),
            },
            questionValidationSchema: condition.questionValidationSchema || {},
        })),
    ];

    const finalPayload = formField && conditions?.length === 0 ? payload[0] : payload;

    const hasMultipleOptions =
        ((fieldData?.questionSchema?.options?.length ?? 0) > 1 &&
            (fieldData.questionSchema.type === 'radio' ||
                fieldData.questionSchema.type === 'multiselect')) ||
        ((fieldData?.questionSchema?.selectOptions?.length ?? 0) > 1 &&
            fieldData.questionSchema.type === 'select');

    const isNotConditional = !formField?.questionSchema?.isConditionalQuestion;
    const isContactBlock = fieldData.questionSchema?.type === 'contactInfoSection';

    const handleSubmit = () => {
        const isTitleUsed =
            conditions.length !==
            new Set(conditions.map((c) => `${c.title}-${c.optionValue}`)).size;

        if (isTitleUsed) {
            errorSnackbar('This question already exists!');
        } else {
            onConfirm(finalPayload as TemplateFormFieldUpdate | TemplateFormFieldUpdate[]);
        }
    };

    const customConditions = Array.from({ length: 10 }, (_, i) => ({
        optionLabel: `${i + 1}`,
        optionValue: `${i + 1}`,
    }));

    // A layout effect reports before paint, so a reopened drawer never shows an enabled submit
    useLayoutEffect(() => {
        onSubmitDisabledChange(isSubmitDisabled);
    }, [isSubmitDisabled, onSubmitDisabledChange]);

    useEffect(() => {
        if (!submitRef) return;
        submitRef.current = { submit: handleSubmit };
    });

    return (
        <>
            <Typography variant="h3">
                {formField ? 'Update your question' : 'Question from scratch'}
            </Typography>
            <Typography variant="body2">
                {formField ? 'Update' : 'Create'} your own question to meet your specific needs.
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    marginTop: '32px',
                    gap: '24px',
                }}
            >
                <TextField
                    label="Question"
                    placeholder="Enter your question"
                    name="title"
                    disabled={!isSuperAdmin && !!formField?.isLocked}
                    value={fieldData.title || ''}
                    onChange={(event) => onChangeTitle(event.target.value)}
                />
                <TextField
                    label="Description"
                    placeholder="Give additional context for the question"
                    name="subTitle"
                    multiline
                    maxRows={10}
                    value={fieldData.subTitle || ''}
                    sx={{
                        '& .MuiInputBase-root': {
                            padding: '10px 14px',
                        },
                    }}
                    onChange={(event) => onChangeDescription(event.target.value)}
                />
                <Select
                    value={
                        contactBlockPreset
                            ? getContactBlockPresetOptionValue(contactBlockPreset)
                            : fieldData.questionSchema?.type
                    }
                    name="type"
                    disabled={
                        formField
                            ? !(
                                  questionConstantType.includes(formField?.questionSchema?.type) &&
                                  isSuperAdmin
                              )
                            : false
                    }
                    sx={{ maxWidth: '200px' }}
                    onChange={(event: SelectChangeEvent<string>) =>
                        onChangeType(event.target.value)
                    }
                >
                    {selectQuestionOptions.flatMap((op) => [
                        <MenuItem key={op.value} value={op.value} disabled={op.isDisabled}>
                            {op.label}
                        </MenuItem>,
                        ...(op.value === 'contactInfoSection'
                            ? CONTACT_BLOCK_PRESET_KINDS.map((kind) => (
                                  <MenuItem
                                      key={kind}
                                      value={getContactBlockPresetOptionValue(kind)}
                                      disabled={op.isDisabled}
                                      sx={{ paddingLeft: '32px' }}
                                  >
                                      {kind} contact block
                                  </MenuItem>
                              ))
                            : []),
                    ])}
                </Select>

                {fieldData.questionSchema?.type === 'currency' && isIntakeFormV2 && (
                    <FormControl sx={{ maxWidth: '200px' }}>
                        <InputLabel>Value Type</InputLabel>
                        <Select
                            value={fieldData.questionSchema?.valueType || ''}
                            name="valueType"
                            label="Value Type"
                            onChange={(event) =>
                                onChangeValueType(toCurrencyValueType(event.target.value))
                            }
                        >
                            <MenuItem value="">N/A</MenuItem>
                            <MenuItem value="asset">Asset</MenuItem>
                            <MenuItem value="debt">Debt</MenuItem>
                        </Select>
                    </FormControl>
                )}

                {showPrefillTags && (
                    <AutoCompleteMultiSelect
                        options={INTAKE_PREFILL_TAG_OPTIONS}
                        getOptionLabel={getIntakePrefillTagLabel}
                        filterOptions={filterPrefillTagOptions}
                        label="Prefill Tags"
                        value={fieldData.questionSchema.tags ?? []}
                        onChange={(_event, value) => onChangeTags(value)}
                        sx={{ maxWidth: '480px' }}
                    />
                )}

                {fieldData.questionSchema?.type === 'select' && (
                    <QuestionSelectComponent
                        fieldData={fieldData}
                        isSuperAdmin={isSuperAdmin}
                        error={isOptionError}
                        onError={(error) => setIsOptionError(error)}
                        disabled={
                            !!fieldData.questionSchema.restrictOptionEdit || !!fieldData?.isLocked
                        }
                        selectOptions={
                            fieldData.questionSchema.selectOptions?.length
                                ? fieldData.questionSchema.selectOptions
                                : [
                                      {
                                          selectItemKey: '',
                                          selectItemLabel: '',
                                          selectItemValue: '',
                                          isNewAdded: true,
                                      },
                                      {
                                          selectItemKey: '',
                                          selectItemLabel: '',
                                          selectItemValue: '',
                                          isNewAdded: true,
                                      },
                                  ]
                        }
                        onChange={onChangeSelectOptions}
                    />
                )}
                {/* Render this component when Multiple choice or Multi select is selected  */}
                {(fieldData.questionSchema?.type === 'radio' ||
                    fieldData.questionSchema?.type === 'multiselect') && (
                    <>
                        <QuestionChoiceComponent
                            isSuperAdmin={isSuperAdmin}
                            error={isOptionError}
                            onError={(error) => setIsOptionError(error)}
                            disabled={
                                !!fieldData.questionSchema.restrictOptionEdit ||
                                !!fieldData?.isLocked
                            }
                            fieldData={fieldData}
                            // choiceOptions={fieldData.questionSchema.options || []}
                            choiceOptions={
                                fieldData.questionSchema.options?.length
                                    ? fieldData.questionSchema.options
                                    : [
                                          {
                                              optionLabel: '',
                                              optionValue: '',
                                              isNewAdded: true,
                                          },
                                          {
                                              optionLabel: '',
                                              optionValue: '',
                                              isNewAdded: true,
                                          },
                                      ]
                            }
                            onChange={onChangeChoiceOptions}
                        />
                        {conditions.map((condition, index) => (
                            <Box key={index} sx={{ marginBottom: '15px' }}>
                                {condition.showOptions && (
                                    <>
                                        <Typography
                                            variant="h3"
                                            sx={{
                                                fontWeight: 'bold',
                                                marginBottom: '15px',
                                            }}
                                        >
                                            Conditional Question #{index + 1}
                                        </Typography>
                                        <FormControl fullWidth>
                                            <InputLabel>
                                                Answer to trigger conditional question
                                            </InputLabel>
                                            <Select
                                                value={condition.optionValue}
                                                name={`conditionalType_${index}`}
                                                onChange={(event) =>
                                                    handleConditionalChange(event, index)
                                                }
                                                label="Answer to trigger conditional question"
                                                sx={{ marginBottom: '10px' }}
                                            >
                                                {fieldData.name ===
                                                    'purchasersListedOnPurchaseAndSaleAgreement' ||
                                                fieldData.name === 'borrowersList' ||
                                                fieldData.name ===
                                                    'sellersListedOnPurchaseAndSaleAgreement'
                                                    ? (fieldData.questionSchema.type === 'radio' ||
                                                          fieldData.questionSchema.type ===
                                                              'multiselect') &&
                                                      customConditions?.map((op, opIndex) => (
                                                          <MenuItem
                                                              key={opIndex}
                                                              value={op.optionLabel}
                                                          >
                                                              {op.optionLabel}
                                                          </MenuItem>
                                                      ))
                                                    : (fieldData.questionSchema.type === 'radio' ||
                                                          fieldData.questionSchema.type ===
                                                              'multiselect') &&
                                                      fieldData.questionSchema?.options?.map(
                                                          (op, opIndex) => (
                                                              <MenuItem
                                                                  key={opIndex}
                                                                  value={op.optionLabel}
                                                              >
                                                                  {op.optionLabel}
                                                              </MenuItem>
                                                          ),
                                                      )}
                                                {fieldData.questionSchema.type === 'select' &&
                                                    fieldData.questionSchema?.selectOptions?.map(
                                                        (op, index) => (
                                                            <MenuItem
                                                                key={index}
                                                                value={op.selectItemLabel}
                                                            >
                                                                {op.selectItemLabel}
                                                            </MenuItem>
                                                        ),
                                                    )}
                                            </Select>
                                        </FormControl>
                                    </>
                                )}
                                {condition.showConditionalType && (
                                    <>
                                        <TextField
                                            label="Question"
                                            placeholder="Enter your question"
                                            name={`title_${index}`}
                                            value={condition.title || ''}
                                            onChange={(event) =>
                                                handleConditionalOnChange(
                                                    index,
                                                    'title',
                                                    event.target.value,
                                                )
                                            }
                                            fullWidth
                                            sx={{ marginBottom: '16px' }}
                                        />
                                        <TextField
                                            label="Description"
                                            placeholder="Give additional context for the question"
                                            name={`subTitle_${index}`}
                                            multiline
                                            maxRows={10}
                                            value={condition.subTitle || ''}
                                            sx={{
                                                '& .MuiInputBase-root': {
                                                    padding: '10px 14px',
                                                },
                                                marginBottom: '16px',
                                            }}
                                            onChange={(event) =>
                                                handleConditionalOnChange(
                                                    index,
                                                    'subTitle',
                                                    event.target.value,
                                                )
                                            }
                                            fullWidth
                                        />
                                        <Select
                                            name={`secondConditionalType_${index}`}
                                            value={condition.conditionalTypeValue}
                                            onChange={(event) =>
                                                handleSecondSelectChange(event, index)
                                            }
                                            fullWidth
                                            sx={{ marginBottom: '10px' }}
                                        >
                                            {conditionalQuestionOptions.map((op, opIndex) => (
                                                <MenuItem key={opIndex} value={op.value}>
                                                    {op.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </>
                                )}
                                {(condition.conditionalTypeValue === 'radio' ||
                                    condition.conditionalTypeValue === 'multiselect') && (
                                    <QuestionChoiceComponent
                                        error={isOptionError}
                                        onError={(error) => setIsOptionError(error)}
                                        disabled={false}
                                        choiceOptions={
                                            condition.conditionalChoiceOptions?.length
                                                ? condition.conditionalChoiceOptions
                                                : [
                                                      {
                                                          optionLabel: '',
                                                          optionValue: '',
                                                          isNewAdded: true,
                                                      },
                                                      {
                                                          optionLabel: '',
                                                          optionValue: '',
                                                          isNewAdded: true,
                                                      },
                                                  ]
                                        }
                                        onChange={(options: QuestionOption[]) => {
                                            const newConditions = [...conditions];
                                            newConditions[index].conditionalChoiceOptions = options;
                                            setConditions(newConditions);
                                        }}
                                    />
                                )}

                                {condition.conditionalTypeValue === 'select' && (
                                    <QuestionSelectComponent
                                        fromConditional={true}
                                        disabled={false}
                                        fieldData={fieldData}
                                        isSuperAdmin={isSuperAdmin}
                                        error={isOptionError}
                                        onError={(error) => setIsOptionError(error)}
                                        selectOptions={
                                            condition.conditionalChoiceOptions?.length
                                                ? condition.conditionalChoiceOptions
                                                : [
                                                      {
                                                          selectItemKey: '',
                                                          selectItemLabel: '',
                                                          selectItemValue: '',
                                                          isNewAdded: true,
                                                      },
                                                      {
                                                          selectItemKey: '',
                                                          selectItemLabel: '',
                                                          selectItemValue: '',
                                                          isNewAdded: true,
                                                      },
                                                  ]
                                        }
                                        onChange={(selectedOptions: SelectOption[]) => {
                                            const newConditions = [...conditions];
                                            newConditions[index].conditionalChoiceOptions =
                                                selectedOptions;
                                            setConditions(newConditions);
                                        }}
                                    />
                                )}

                                {condition.conditionalTypeValue === 'upload' && (
                                    <>
                                        <TextField
                                            label="Type of Document"
                                            name={`typeOfDocument_${index}`}
                                            value={condition.typeOfDocument || ''}
                                            onChange={(event) =>
                                                handleConditionalOnChange(
                                                    index,
                                                    'typeOfDocument',
                                                    event.target.value,
                                                )
                                            }
                                        />
                                        <UploadFileCountField
                                            value={
                                                condition.questionValidationSchema?.onChange
                                                    ?.fileCount ?? UNSET_UPLOAD_FILE_COUNT
                                            }
                                            onChange={(fileCount) =>
                                                onChangeConditionalUploadFileCount(index, fileCount)
                                            }
                                        />
                                    </>
                                )}
                                {condition.conditionalTypeValue === 'currency' &&
                                    isIntakeFormV2 && (
                                        <FormControl
                                            sx={{
                                                minWidth: 200,
                                                marginBottom: '16px',
                                            }}
                                        >
                                            <InputLabel>Value Type</InputLabel>
                                            <Select
                                                value={condition.valueType || ''}
                                                label="Value Type"
                                                onChange={(event) =>
                                                    onChangeConditionalValueType(
                                                        index,
                                                        toCurrencyValueType(event.target.value),
                                                    )
                                                }
                                            >
                                                <MenuItem value="">N/A</MenuItem>
                                                <MenuItem value="asset">Asset</MenuItem>
                                                <MenuItem value="debt">Debt</MenuItem>
                                            </Select>
                                        </FormControl>
                                    )}
                                {showPrefillTags && (
                                    <AutoCompleteMultiSelect
                                        options={INTAKE_PREFILL_TAG_OPTIONS}
                                        getOptionLabel={getIntakePrefillTagLabel}
                                        filterOptions={filterPrefillTagOptions}
                                        label="Prefill Tags"
                                        value={condition.tags ?? []}
                                        onChange={(_event, value) =>
                                            onChangeConditionalTags(index, value)
                                        }
                                        sx={{
                                            maxWidth: '480px',
                                            marginBottom: '16px',
                                        }}
                                    />
                                )}
                                {condition.conditionalTypeValue === 'table' && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '16px',
                                        }}
                                    >
                                        <TableRowCountField
                                            value={condition.conditionalRowCount}
                                            onChange={(rowCount) => {
                                                const newConditions = [...conditions];
                                                newConditions[index].conditionalRowCount = rowCount;
                                                setConditions(newConditions);
                                            }}
                                        />
                                        <TableColumnsComponent
                                            isSuperAdmin={isSuperAdmin}
                                            disabled={false}
                                            columns={
                                                (condition.conditionalColumns?.length ?? 0) > 0
                                                    ? (condition.conditionalColumns as TableFormColumn[])
                                                    : [
                                                          {
                                                              key: '',
                                                              label: '',
                                                              type: 'text',
                                                          },
                                                      ]
                                            }
                                            onChange={(columns: TableFormColumn[]) => {
                                                const newConditions = [...conditions];
                                                newConditions[index].conditionalColumns = columns;
                                                setConditions(newConditions);
                                            }}
                                        />
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </>
                )}

                {fieldData.questionSchema?.type === 'select' &&
                    conditions.map((condition, index) => (
                        <Box key={index} sx={{ marginBottom: '15px' }}>
                            {condition.showOptions && (
                                <>
                                    <Typography
                                        variant="h3"
                                        sx={{
                                            fontWeight: 'bold',
                                            marginBottom: '15px',
                                        }}
                                    >
                                        Conditional Question #{index + 1}
                                    </Typography>
                                    <FormControl fullWidth>
                                        <InputLabel>
                                            Answer to trigger conditional question
                                        </InputLabel>
                                        <Select
                                            value={condition.optionValue}
                                            name={`conditionalType_${index}`}
                                            onChange={(event) =>
                                                handleConditionalChange(event, index)
                                            }
                                            label="Answer to trigger conditional question"
                                            sx={{ marginBottom: '10px' }}
                                        >
                                            {fieldData.questionSchema.type === 'select' &&
                                                fieldData.questionSchema?.selectOptions?.map(
                                                    (op, index) => (
                                                        <MenuItem
                                                            key={index}
                                                            value={op.selectItemLabel}
                                                        >
                                                            {op.selectItemLabel}
                                                        </MenuItem>
                                                    ),
                                                )}
                                        </Select>
                                    </FormControl>
                                </>
                            )}
                            {condition.showConditionalType && (
                                <>
                                    <TextField
                                        label="Question"
                                        placeholder="Enter your question"
                                        name={`title_${index}`}
                                        value={condition.title || ''}
                                        onChange={(event) =>
                                            handleConditionalOnChange(
                                                index,
                                                'title',
                                                event.target.value,
                                            )
                                        }
                                        fullWidth
                                        sx={{ marginBottom: '16px' }}
                                    />
                                    <TextField
                                        label="Description"
                                        placeholder="Give additional context for the question"
                                        name={`subTitle_${index}`}
                                        multiline
                                        maxRows={10}
                                        value={condition.subTitle || ''}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                padding: '10px 14px',
                                            },
                                            marginBottom: '16px',
                                        }}
                                        onChange={(event) =>
                                            handleConditionalOnChange(
                                                index,
                                                'subTitle',
                                                event.target.value,
                                            )
                                        }
                                        fullWidth
                                    />
                                    <Select
                                        name={`secondConditionalType_${index}`}
                                        value={condition.conditionalTypeValue}
                                        onChange={(event) => handleSecondSelectChange(event, index)}
                                        fullWidth
                                        sx={{ marginBottom: '10px' }}
                                    >
                                        {conditionalQuestionOptions.map((op, opIndex) => (
                                            <MenuItem key={opIndex} value={op.value}>
                                                {op.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </>
                            )}
                            {(condition.conditionalTypeValue === 'radio' ||
                                condition.conditionalTypeValue === 'multiselect') && (
                                <QuestionChoiceComponent
                                    error={isOptionError}
                                    onError={(error) => setIsOptionError(error)}
                                    disabled={false}
                                    choiceOptions={
                                        condition.conditionalChoiceOptions?.length
                                            ? condition.conditionalChoiceOptions
                                            : [
                                                  {
                                                      optionLabel: '',
                                                      optionValue: '',
                                                      isNewAdded: true,
                                                  },
                                                  {
                                                      optionLabel: '',
                                                      optionValue: '',
                                                      isNewAdded: true,
                                                  },
                                              ]
                                    }
                                    onChange={(options: QuestionOption[]) => {
                                        const newConditions = [...conditions];
                                        newConditions[index].conditionalChoiceOptions = options;
                                        setConditions(newConditions);
                                    }}
                                />
                            )}
                            {condition.conditionalTypeValue === 'upload' && (
                                <>
                                    <TextField
                                        label="Type of Document"
                                        name={`typeOfDocument_${index}`}
                                        value={condition.typeOfDocument || ''}
                                        onChange={(event) =>
                                            handleConditionalOnChange(
                                                index,
                                                'typeOfDocument',
                                                event.target.value,
                                            )
                                        }
                                    />
                                    <UploadFileCountField
                                        value={
                                            condition.questionValidationSchema?.onChange
                                                ?.fileCount ?? UNSET_UPLOAD_FILE_COUNT
                                        }
                                        onChange={(fileCount) =>
                                            onChangeConditionalUploadFileCount(index, fileCount)
                                        }
                                    />
                                </>
                            )}
                            {condition.conditionalTypeValue === 'currency' && isIntakeFormV2 && (
                                <FormControl
                                    sx={{
                                        minWidth: 200,
                                        marginBottom: '16px',
                                    }}
                                >
                                    <InputLabel>Value Type</InputLabel>
                                    <Select
                                        value={condition.valueType || ''}
                                        label="Value Type"
                                        onChange={(event) =>
                                            onChangeConditionalValueType(
                                                index,
                                                toCurrencyValueType(event.target.value),
                                            )
                                        }
                                    >
                                        <MenuItem value="">N/A</MenuItem>
                                        <MenuItem value="asset">Asset</MenuItem>
                                        <MenuItem value="debt">Debt</MenuItem>
                                    </Select>
                                </FormControl>
                            )}
                            {showPrefillTags && (
                                <AutoCompleteMultiSelect
                                    options={INTAKE_PREFILL_TAG_OPTIONS}
                                    getOptionLabel={getIntakePrefillTagLabel}
                                    filterOptions={filterPrefillTagOptions}
                                    label="Prefill Tags"
                                    value={condition.tags ?? []}
                                    onChange={(_event, value) =>
                                        onChangeConditionalTags(index, value)
                                    }
                                    sx={{ maxWidth: '480px', marginBottom: '16px' }}
                                />
                            )}
                            {condition.conditionalTypeValue === 'select' && (
                                <QuestionSelectComponent
                                    fromConditional={true}
                                    disabled={false}
                                    fieldData={fieldData}
                                    isSuperAdmin={isSuperAdmin}
                                    error={isOptionError}
                                    onError={(error) => setIsOptionError(error)}
                                    selectOptions={
                                        condition.conditionalChoiceOptions?.length
                                            ? condition.conditionalChoiceOptions
                                            : [
                                                  {
                                                      selectItemKey: '',
                                                      selectItemLabel: '',
                                                      selectItemValue: '',
                                                      isNewAdded: true,
                                                  },
                                                  {
                                                      selectItemKey: '',
                                                      selectItemLabel: '',
                                                      selectItemValue: '',
                                                      isNewAdded: true,
                                                  },
                                              ]
                                    }
                                    onChange={(selectedOptions: SelectOption[]) => {
                                        const newConditions = [...conditions];
                                        newConditions[index].conditionalChoiceOptions =
                                            selectedOptions;
                                        setConditions(newConditions);
                                    }}
                                />
                            )}
                            {condition.conditionalTypeValue === 'table' && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '16px',
                                    }}
                                >
                                    <TableRowCountField
                                        value={condition.conditionalRowCount}
                                        onChange={(rowCount) => {
                                            const newConditions = [...conditions];
                                            newConditions[index].conditionalRowCount = rowCount;
                                            setConditions(newConditions);
                                        }}
                                    />
                                    <TableColumnsComponent
                                        isSuperAdmin={isSuperAdmin}
                                        disabled={false}
                                        columns={
                                            (condition.conditionalColumns?.length ?? 0) > 0
                                                ? (condition.conditionalColumns as TableFormColumn[])
                                                : [{ key: '', label: '', type: 'text' }]
                                        }
                                        onChange={(columns: TableFormColumn[]) => {
                                            const newConditions = [...conditions];
                                            newConditions[index].conditionalColumns = columns;
                                            setConditions(newConditions);
                                        }}
                                    />
                                </Box>
                            )}
                        </Box>
                    ))}

                {hasMultipleOptions && isNotConditional && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box sx={{ marginLeft: '-15px' }}>
                            <Button
                                sx={{
                                    textTransform: 'initial',
                                    textAlign: 'left',
                                }}
                                variant="text"
                                onClick={handleAddConditional}
                                disabled={conditions.some(
                                    (condition) =>
                                        (condition.conditionalTypeValue === 'radio' ||
                                            condition.conditionalTypeValue === 'multiselect') &&
                                        (!condition.conditionalChoiceOptions ||
                                            condition.conditionalChoiceOptions.length < 2 ||
                                            condition.conditionalChoiceOptions.some(
                                                (option) => !option.optionLabel,
                                            )),
                                )}
                            >
                                <AddCircleOutlineOutlined />
                                Add conditional question
                            </Button>
                        </Box>
                        {conditions?.length > 0 && (
                            <Box>
                                <Button
                                    sx={{
                                        textTransform: 'initial',
                                    }}
                                    variant="text"
                                    onClick={handleRemoveConditional}
                                >
                                    <RemoveCircleOutline /> Remove conditional question
                                </Button>
                            </Box>
                        )}
                    </Box>
                )}
                {/* Render this component when Upload field is selected  */}
                {fieldData.questionSchema?.type === 'upload' && (
                    <>
                        <TextField
                            label="Type of Document"
                            name="typeOfDocument"
                            value={fieldData?.questionSchema?.typeOfDocument || ''}
                            onChange={(
                                event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                            ) => {
                                onChangeTypeOfDocument(event.target.value);
                            }}
                        />
                        <UploadFileCountField
                            value={
                                fieldData.questionValidationSchema?.onChange?.fileCount ??
                                UNSET_UPLOAD_FILE_COUNT
                            }
                            disabled={!!fieldData?.isLocked && !isSuperAdmin}
                            onChange={onChangeUploadFileCount}
                        />
                    </>
                )}
                {fieldData.questionSchema?.type === 'contactInfoSection' && (
                    <ContactBlockConfigEditor
                        config={getContactBlockConfig(fieldData.questionSchema)}
                        language={language}
                        practiceArea={practiceArea}
                        matterType={matterType}
                        disabled={!!fieldData?.isLocked && !isSuperAdmin}
                        onChange={onChangeContactBlock}
                    />
                )}
                {/* Render this component when Table is selected */}
                {fieldData.questionSchema?.type === 'table' && (
                    <>
                        <TableRowCountField
                            disabled={!!fieldData?.isLocked}
                            value={fieldData.questionSchema?.rowCount}
                            onChange={onChangeTableRowCount}
                        />
                        <TableColumnsComponent
                            isSuperAdmin={isSuperAdmin}
                            disabled={!!fieldData?.isLocked}
                            columns={
                                (fieldData.questionSchema?.columns?.length ?? 0) > 0
                                    ? (fieldData.questionSchema.columns as TableFormColumn[])
                                    : [{ key: '', label: '', type: 'text' }]
                            }
                            onChange={onChangeTableColumns}
                        />
                    </>
                )}
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    marginTop: '32px',
                    gap: '24px',
                }}
            >
                <FormControlLabel
                    control={
                        <Checkbox
                            name="requiredCheckbox"
                            checked={!!fieldData?.questionValidationSchema?.required}
                            disabled={!!fieldData?.isLocked && !isSuperAdmin}
                            onChange={(e) => {
                                onChangeRequired(e.target.checked);
                            }}
                        />
                    }
                    label={isContactBlock ? 'Required (at least one entry)' : 'Required'}
                />
                {isSuperAdmin && !isNestedQuestion && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                name="lockedCheckbox"
                                checked={!!fieldData?.isLocked}
                                onChange={(e) => {
                                    onChangeLocked(e.target.checked);
                                }}
                            />
                        }
                        label="Locked"
                    />
                )}
            </Box>
        </>
    );
}
